import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { usePortfolioSummary } from "@/hooks/usePortfolioSummary";
import KpiCard from "./KpiCard";
import { useExchangeRates } from "@/hooks/useExchangeRates";
import { formatCurrency, formatNumber } from "@/lib/currency.utils";
import { CurrencyToggle } from "@/components/currency-toggle/CurrencyToggle";
import { useNetWorthSnapshots } from "@/hooks/useNetWorthSnapshots";
import { useMemo } from "react";


export function PortfolioSummaryPanel() {
  const { data, isLoading } = usePortfolioSummary();
  const [displayCurrency, setDisplayCurrency] = useState<string>("KES");
  const {
    convert,
    isLoading: ratesLoading,
    error: ratesError,
  } = useExchangeRates(displayCurrency);

  const summary = data ?? {
    totalIncome: 0,
    totalExpenses: 0,
    netSavings: 0,
    savingsRate: 0,
    momExpenseChange: 0,
    totalAccountBalance: 0,
    currency: "KES", // - defaulted for now
  };

  const { data: snapshots = [], isLoading: snapsLoading } = useNetWorthSnapshots("1y");

  const trendData = useMemo(() => {
    return Array.from({ length: 12 }, (_, i) => {
      const d = new Date();
      d.setMonth(d.getMonth() - (11 - i));
      const monthKey = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      
      let monthNetWorth = 0;
      if (snapshots.length > 0) {
        const monthSnaps = snapshots.filter(s => s.snapshot_date.startsWith(monthKey));
        if (monthSnaps.length > 0) {
          const latest = monthSnaps.reduce((prev, curr) => curr.snapshot_date > prev.snapshot_date ? curr : prev);
          monthNetWorth = convert(latest.net_worth, latest.currency, displayCurrency);
        }
      }

      return {
        month: d.toLocaleString("default", { month: "short", year: "2-digit" }),
        netWorth: monthNetWorth,
      };
    });
  }, [snapshots, convert, displayCurrency]);

  const isDataLoading = isLoading || snapsLoading;

  const formattedTotalIncomeValue = formatCurrency(
    convert(summary.totalIncome, summary.currency, displayCurrency),
    displayCurrency,
  );
  const formattedTotalExpensesValue = formatCurrency(
    convert(summary.totalExpenses, summary.currency, displayCurrency),
    displayCurrency,
  );
  const formattedNetSavingsValue = formatCurrency(
    convert(summary.netSavings, summary.currency, displayCurrency),
    displayCurrency,
  );
  const formattedTotalAccountBalanceValue = formatCurrency(
    convert(summary.totalAccountBalance, summary.currency, displayCurrency),
    displayCurrency,
  );

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-wrap items-center justify-between gap-4">
          <CardTitle>Portfolio Summary</CardTitle>
          <CurrencyToggle
            baseCurrency={displayCurrency}
            selectedCurrency={displayCurrency}
            onCurrencyChange={setDisplayCurrency}
            disabled={ratesLoading}
            showBalance={false}
          />
        </div>
      </CardHeader>
      <CardContent>
        {isDataLoading && (
          <p className="text-sm text-muted-foreground">Loading...</p>
        )}

        {!isDataLoading && (
          <div className="space-y-6">
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <KpiCard label="Total Income" value={formattedTotalIncomeValue} />
              <KpiCard
                label="Total Expenses"
                value={formattedTotalExpensesValue}
              />
              <KpiCard label="Net Savings" value={formattedNetSavingsValue} />
              <KpiCard
                label="Savings Rate"
                value={`${summary.savingsRate.toFixed(1)}%`}
              />
              <KpiCard
                label="MoM Expense Change"
                value={`${summary.momExpenseChange.toFixed(1)}%`}
              />
              <KpiCard
                label="Total Account Balance"
                value={formattedTotalAccountBalanceValue}
              />
            </div>

            <div aria-label="Net worth trend" className="h-48">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={trendData}>
                  <XAxis dataKey="month" />
                  <YAxis tickFormatter={(value) => formatNumber(value, 2)} />
                  <Tooltip formatter={(value: number) => formatNumber(value, 2)} />
                  <Line
                    type="monotone"
                    dataKey="netWorth"
                    stroke="#6366f1"
                    dot={false}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
